<template>
  <div class="login-shell">
    <section class="login-hero">
      <div class="brand-block">
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Welcome Back</h1>
        <p class="hero-copy">
          Sign in to manage sales, inventory, cash control, reports, and nightly service.
        </p>
      </div>

      <div class="login-card">
        <div class="card-topline"></div>

        <!-- LOGIN -->
        <div v-if="!showRegistration && !showForgotPassword && !twofaSetupRequired && !twofaRequired">
          <div class="form-heading">
            <p class="eyebrow">Access</p>
            <h2>Login</h2>
            <p>Use your LotLedger credentials to continue.</p>
          </div>

          <div class="form-stack">
            <label>
              <span>Username</span>
              <input
                v-model="username"
                placeholder="Username"
                autocomplete="username"
                @keyup.enter="login"
              />
            </label>

            <label>
              <span>Password</span>
              <input
                type="password"
                v-model="password"
                placeholder="Password"
                autocomplete="current-password"
                @keyup.enter="login"
              />
            </label>

            <button type="button" @click="login" class="primary-action">
              Login
            </button>
          </div>

          <button
            type="button"
            @click="showRegistration = true"
            class="toggle-link"
          >
            Don’t have an account? Register here.
          </button>

          <button
            type="button"
            @click="showForgotPassword = true; showRegistration = false"
            class="toggle-link"
          >
            Forgot password?
          </button>
        </div>

        <!-- REGISTER -->
        <div v-else-if="showRegistration && !twofaSetupRequired && !twofaRequired">
          <div class="form-heading">
            <p class="eyebrow">New User</p>
            <h2>Register</h2>
            <p>Create an account for Wine Bar access.</p>
          </div>

          <div class="form-stack">
            <label>
              <span>Username</span>
              <input
                v-model="regUsername"
                placeholder="Username"
                autocomplete="username"
              />
            </label>

            <label>
              <span>Password</span>
              <input
                type="password"
                v-model="regPassword"
                placeholder="Password"
                autocomplete="new-password"
              />
            </label>

            

            <button type="button" @click="register" class="primary-action">
              Register
            </button>
          </div>

          <button
            type="button"
            @click="showRegistration = false"
            class="toggle-link"
          >
            Already have an account? Login here.
          </button>
        </div>

        <!-- FORGOT PASSWORD -->
        <div v-else-if="showForgotPassword && !twofaSetupRequired && !twofaRequired">
          <div class="form-heading">
            <p class="eyebrow">Account Help</p>
            <h2>Reset Password</h2>
            <p>
              Enter your username, authenticator code, and new password.
            </p>
          </div>

          <div class="form-stack">
            <label>
              <span>Username</span>
              <input
                v-model="forgotUsername"
                placeholder="Username"
                autocomplete="username"
              />
            </label>

            <label>
              <span>Authenticator Code</span>
              <input
                v-model="forgotToken"
                placeholder="123456"
                inputmode="numeric"
                autocomplete="one-time-code"
              />
            </label>

            <label>
              <span>New Password</span>
              <input
                type="password"
                v-model="forgotNewPassword"
                placeholder="New password"
                autocomplete="new-password"
                @keyup.enter="resetForgottenPassword"
              />
            </label>

            <button
              type="button"
              @click="resetForgottenPassword"
              class="primary-action"
            >
              Reset Password
            </button>
          </div>

          <button
            type="button"
            @click="showForgotPassword = false"
            class="toggle-link"
          >
            Back to login
          </button>
        </div>

        <!-- 2FA SETUP -->
        <div v-else-if="twofaSetupRequired">
          <div class="form-heading">
            <p class="eyebrow">Security</p>
            <h2>Set Up Two-Factor Authentication</h2>
            <p>
              Scan the QR code with Google Authenticator, then enter the 6-digit code.
            </p>
          </div>

          <div class="twofa-panel">
            <img
              :src="qrCodeDataURL"
              alt="QR Code"
              v-if="qrCodeDataURL"
              class="qr-code"
            />

            <div class="secret-box">
              <span>Manual Secret</span>
              <strong>{{ secret }}</strong>
            </div>
          </div>

          <div class="form-stack">
            <label>
              <span>Authenticator Code</span>
              <input
                v-model="twofaCode"
                placeholder="123456"
                inputmode="numeric"
                autocomplete="one-time-code"
                @keyup.enter="verify2FASetup"
              />
            </label>

            <button type="button" @click="verify2FASetup" class="primary-action">
              Verify 2FA Setup
            </button>
          </div>
        </div>

        <!-- 2FA VERIFY -->
        <div v-else-if="twofaRequired">
          <div class="form-heading">
            <p class="eyebrow">Security</p>
            <h2>Two-Factor Authentication</h2>
            <p>Enter the 6-digit code from your authenticator app.</p>
          </div>

          <div class="form-stack">
            <label>
              <span>Authenticator Code</span>
              <input
                v-model="twofaCode"
                placeholder="123456"
                inputmode="numeric"
                autocomplete="one-time-code"
                @keyup.enter="verify2FA"
              />
            </label>

            <button type="button" @click="verify2FA" class="primary-action">
              Verify 2FA
            </button>
          </div>
        </div>

        <!-- MESSAGES -->
        <div v-if="errorMessage" class="message-card error-card">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="message-card success-card">
          {{ successMessage }}
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "LoginPage",

  data() {
    return {
      username: "",
      password: "",

      regUsername: "",
      regPassword: "",
      regPhoneNumber: "",

      twofaCode: "",
      twofaRequired: false,
      twofaSetupRequired: false,
      qrCodeDataURL: "",
      secret: "",

      showForgotPassword: false,
      forgotUsername: "",
      forgotToken: "",
      forgotNewPassword: "",

      errorMessage: "",
      successMessage: "",
      showRegistration: false
    };
  },

  methods: {
    resetMessages() {
      this.errorMessage = "";
      this.successMessage = "";
    },

    resetTwoFactorState() {
      this.twofaRequired = false;
      this.twofaSetupRequired = false;
      this.qrCodeDataURL = "";
      this.secret = "";
      this.twofaCode = "";
    },

    storeLoginSession(user) {
      sessionStorage.setItem("authenticated", "true");

      if (user) {
        sessionStorage.setItem("user", JSON.stringify(user));
      }

      if (this.$root) {
        this.$root.isAuthenticated = true;
      }
    },

    async login() {
      this.resetMessages();
      this.resetTwoFactorState();

      try {
        const response = await api.post("/login", {
          username: this.username,
          password: this.password
        });

        if (response.data.twofaRequired) {
          this.twofaRequired = true;
          this.successMessage = "2FA required. Enter the code from your app.";
          return;
        }

        if (response.data.twofaSetupRequired) {
          await this.setup2FA();
          return;
        }

        if (response.data.success) {
          this.storeLoginSession(response.data.user);

          this.successMessage = "Login successful!";
          this.$router.push("/home");
          return;
        }

        this.errorMessage = "Unexpected login response.";
      } catch (error) {
        this.errorMessage = error.response?.data?.error || "Login failed.";
      }
    },

    async register() {
      this.resetMessages();

      try {
        const response = await api.post("/register", {
          username: this.regUsername,
          password: this.regPassword,
          phone_number: this.regPhoneNumber
        });

        if (response.data.success) {
          this.successMessage =
            response.data.message ||
            "Registration submitted. An administrator must activate your account before you can log in.";

          this.showRegistration = false;
          this.regUsername = "";
          this.regPassword = "";
          this.regPhoneNumber = "";
          return;
        }

        this.errorMessage = "Registration failed.";
      } catch (error) {
        this.errorMessage = error.response?.data?.error || "Registration failed.";
      }
    },

    async setup2FA() {
      try {
        const response = await api.post("/2fa/setup", {
          username: this.username
        });

        this.qrCodeDataURL = response.data.qrCodeDataURL;
        this.secret = response.data.secret;
        this.twofaSetupRequired = true;
      } catch (error) {
        this.errorMessage = error.response?.data?.error || "Failed to setup 2FA.";
      }
    },

    async verify2FA() {
      this.resetMessages();

      try {
        const response = await api.post("/2fa/verify", {
          username: this.username,
          token: this.twofaCode
        });

        if (response.data.success) {
          this.storeLoginSession(response.data.user);

          this.successMessage = "2FA verification successful!";
          this.$router.push("/home");
          return;
        }

        this.errorMessage = "Invalid 2FA code.";
      } catch (error) {
        this.errorMessage = error.response?.data?.error || "2FA verification failed.";
      }
    },

    async verify2FASetup() {
      await this.verify2FA();
    },

    async resetForgottenPassword() {
      this.resetMessages();

      try {
        const response = await api.put("/reset-password", {
          username: this.forgotUsername,
          token: this.forgotToken,
          newPassword: this.forgotNewPassword
        });

        if (response.data.success) {
          this.successMessage =
            response.data.message || "Password reset successfully. You may now log in.";

          this.showForgotPassword = false;
          this.forgotUsername = "";
          this.forgotToken = "";
          this.forgotNewPassword = "";
          return;
        }

        this.errorMessage = "Password reset failed.";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "Password reset failed.";
      }
    }
  }
};
</script>

<style scoped>
.login-shell {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 28px;
  color: #fff8f1;
  font-family: "Poppins", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 34%),
    radial-gradient(circle at bottom right, rgba(200, 166, 106, 0.14), transparent 28%),
    linear-gradient(135deg, #120b10, #22131a 48%, #0f0a0d);
}

.login-hero {
  width: min(1120px, 100%);
  display: grid;
  grid-template-columns: minmax(320px, 1fr) 430px;
  gap: 28px;
  align-items: center;
}

.brand-block {
  padding: 34px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.brand-block h1 {
  margin: 0;
  font-size: clamp(56px, 8vw, 104px);
  line-height: 0.86;
  letter-spacing: -0.085em;
}

.hero-copy {
  max-width: 580px;
  margin: 22px 0 0;
  color: #d8cbc4;
  font-size: 17px;
  line-height: 1.55;
}

.login-card {
  position: relative;
  overflow: hidden;
  padding: 28px;
  border-radius: 32px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.095), rgba(255, 255, 255, 0.035));
  border: 1px solid rgba(255, 255, 255, 0.105);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.32);
  backdrop-filter: blur(18px);
}

.card-topline {
  position: absolute;
  inset: 0 0 auto 0;
  height: 5px;
  background: linear-gradient(90deg, #f1c979, #c8a66a, #7c1838);
}

.form-heading {
  margin-bottom: 22px;
}

.form-heading h2 {
  margin: 0;
  font-size: 42px;
  line-height: 0.95;
  letter-spacing: -0.065em;
}

.form-heading p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.form-stack {
  display: grid;
  gap: 14px;
}

.form-stack label {
  display: grid;
  gap: 8px;
}

.form-stack label span {
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

input {
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
  padding: 0 15px;
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(8, 5, 8, 0.62);
  color: #fff8f1;
  font-family: inherit;
  font-size: 15px;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
}

input::placeholder {
  color: rgba(255, 248, 241, 0.42);
}

input:focus {
  border-color: rgba(241, 201, 121, 0.72);
  box-shadow: 0 0 0 4px rgba(241, 201, 121, 0.12);
  background: rgba(8, 5, 8, 0.78);
}

.primary-action {
  min-height: 48px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  color: #1b1015;
  background: linear-gradient(135deg, #f1c979, #c8a66a);
  cursor: pointer;
  font-family: inherit;
  font-weight: 900;
  letter-spacing: -0.02em;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.22);
  transition:
    transform 0.15s ease,
    filter 0.15s ease;
}

.primary-action:hover {
  transform: translateY(-1px);
  filter: brightness(1.04);
}

.primary-action:active {
  transform: translateY(0);
}

.toggle-link {
  width: 100%;
  margin: 18px 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f1c979;
  cursor: pointer;
  font-family: inherit;
  font-weight: 800;
  text-align: center;
}

.toggle-link:hover {
  text-decoration: underline;
}

.twofa-panel {
  display: grid;
  gap: 16px;
  margin-bottom: 18px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.qr-code {
  width: 210px;
  height: 210px;
  justify-self: center;
  padding: 12px;
  border-radius: 20px;
  background: #fff;
}

.secret-box {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.secret-box span {
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.secret-box strong {
  overflow-wrap: anywhere;
  color: #f1c979;
  font-size: 14px;
  line-height: 1.45;
}

.message-card {
  margin-top: 16px;
  padding: 13px 14px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.35;
}

.error-card {
  color: #ffd8d1;
  background: rgba(255, 83, 73, 0.13);
  border: 1px solid rgba(255, 142, 122, 0.26);
}

.success-card {
  color: #d9ffe8;
  background: rgba(78, 191, 126, 0.13);
  border: 1px solid rgba(120, 224, 160, 0.25);
}

@media (max-width: 860px) {
  .login-shell {
    padding: 18px;
    align-items: start;
  }

  .login-hero {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .brand-block {
    padding: 14px 6px;
  }

  .brand-block h1 {
    font-size: 58px;
  }

  .hero-copy {
    margin-top: 14px;
    font-size: 15px;
  }

  .login-card {
    padding: 22px;
    border-radius: 26px;
  }
}

@media (max-width: 520px) {
  .login-shell {
    padding: 12px;
  }

  .brand-block h1 {
    font-size: 48px;
  }

  .form-heading h2 {
    font-size: 34px;
  }

  .qr-code {
    width: 180px;
    height: 180px;
  }
}
</style>